/**
 * Agrupación de subsecciones dinámicas alineada con
 * TareaRepository.newIndexListaProcedimiento (categoria + listIndex + listLength).
 */

const GROUP_SEP = '\u0000';

export function buildDynamicSubsectionGroupKey(item: {
  categoria?: string;
  listIndex?: unknown;
  listLength?: unknown;
}): string | null {
  const listIndex = Number(item.listIndex);
  const listLength = Number(item.listLength);
  if (!Number.isFinite(listIndex) || !Number.isFinite(listLength)) {
    return null;
  }
  return `${String(item.categoria ?? '')}${GROUP_SEP}${listIndex}${GROUP_SEP}${listLength}`;
}

export function matchesDynamicSubsectionGroup(item: any, ref: any): boolean {
  if (item?.tipo !== 'subseccion' || item?.isDynamic !== true) {
    return false;
  }
  const refKey = buildDynamicSubsectionGroupKey(ref);
  if (!refKey) return false;
  return buildDynamicSubsectionGroupKey(item) === refKey;
}

/** Columnas de la tabla: primeras `listLength` preguntas tras la cabecera (como newIndex). */
export function getDynamicSubsectionColumnItems(lista: any[], headerIndex: number): any[] {
  const header = lista[headerIndex];
  const listLength = Number(header?.listLength);
  if (!Number.isFinite(listLength) || listLength < 1) {
    return [];
  }
  const items: any[] = [];
  let j = headerIndex + 1;
  let preguntasTomadas = 0;
  while (j < lista.length && preguntasTomadas < listLength) {
    const x = lista[j];
    if (x?.tipo === 'subseccion' || x?.tipo === 'seccion') {
      break;
    }
    if (x?.tipo === 'pregunta') {
      if (x?.pregunta?.label && !x?.isTaskExecutorFromSource) {
        items.push(x);
      }
      preguntasTomadas += 1;
    }
    j += 1;
  }
  return items;
}

export function countDynamicSubsectionHeaders(lista: any[], ref: any): number {
  return lista.filter((item) => matchesDynamicSubsectionGroup(item, ref)).length;
}

/** Cabecera dinámica que posee el ítem en `itemIndex` (ventana de listLength preguntas). */
export function findDynamicSubsectionHeaderForItem(lista: any[], itemIndex: number): any | null {
  const arr = lista || [];
  let i = 0;
  while (i < arr.length) {
    const item = arr[i];
    if (item?.tipo === 'subseccion' && item?.isDynamic === true) {
      const listIndex = Number(item.listIndex);
      const listLength = Number(item.listLength);
      if (!Number.isFinite(listIndex) || !Number.isFinite(listLength) || listLength < 1) {
        i += 1;
        continue;
      }
      let j = i + 1;
      let preguntasTomadas = 0;
      while (j < arr.length && preguntasTomadas < listLength) {
        const x = arr[j];
        if (x?.tipo === 'subseccion' || x?.tipo === 'seccion') {
          break;
        }
        if (x?.tipo === 'pregunta') {
          if (j === itemIndex) {
            return item;
          }
          preguntasTomadas += 1;
        }
        j += 1;
      }
      i = j;
      continue;
    }
    i += 1;
  }
  return null;
}

export const PLASTYVERG_TENANT_ID = '6952e2ae5d0d13b6a5b080c9';

export function isPlastyvergTenant(tenantId: string | undefined | null): boolean {
  return tenantId === PLASTYVERG_TENANT_ID;
}

/** Etiqueta visible en cabecera de columna dinámica (p. ej. acortar DESBLOQUEADA en Plastyverg). */
export function formatDynamicSubsectionColumnLabelForPdf(label: string | undefined | null, isPlastyverg: boolean): string {
  const text = String(label ?? '');
  if (isPlastyverg && text.trim().toUpperCase() === 'DESBLOQUEADA') {
    return 'DESBLOQ';
  }
  if (isPlastyverg && text.trim().toUpperCase() === 'COLOR / TRANSPARENCIA') {
    return 'COLOR / TRANSP';
  }
  return text;
}

/** Si el ítem pertenece a un bloque dinámico (modo tabla horizontal / omitir vertical). */
export function isItemInDynamicSubsectionForPdf(lista: any[], itemIndex: number, elem: any): boolean {
  if (elem?.isDynamic && (elem?.tipo === 'subseccion' || elem?.tipo === 'seccion')) {
    return true;
  }
  return !!findDynamicSubsectionHeaderForItem(lista, itemIndex);
}
